// React & React Hooks
import { useState, useEffect, useContext } from "react";

// Third-party libraries
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

// MUI components
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  Stack,
  Chip,
  IconButton,
  Pagination,
} from "@mui/material";

// MUI icons
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";

// Constants
import dataConstant from "../../constants/dataConstants";
import urlConstant from "../../constants/urlConstants";

// Services
import { httpPost, DecryptData } from "../../services/commonService";

// Custom components
import CustomTextField from "../../shared-components/form-controls/customTextField";
import CustomTextFieldComponent from "../../shared-components/form-controls/customTextField";
import CustomAutoCompleteDropdown from "../../shared-components/form-controls/customAutoCompleteDropdown";
import CommonSnackBar from "../snackbar/commonSnackbar";

// Context and Redux
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";
import { selectUserDetail } from "../../redux/slice/userSlice";
import SnackState from "../snackbar/snackState";
import moment from "moment";

const TravelVertexDiscountCardManagement = () => {
  const { t } = useTranslation();
  const userDetail = useSelector(selectUserDetail);
  const authenticationState = useContext(AuthenticationContext);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [localCards, setLocalCards] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardInfo, setSelectedCardInfo] = useState(null);
  const [pinRevealed, setPinRevealed] = useState({});
  const [filters, setFilters] = useState({
    keyWord: "",
    cardStatus: "",
    cardNumber: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [membershipLevel, setMembershipLevel] = useState("");
  const [cardsAvailable, setCardsAvailable] = useState(0);
  const [sendableStatuses] = useState([
    dataConstant.TravelVertexDiscountCardStatus.AVAILABLE,
    dataConstant.TravelVertexDiscountCardStatus.ACTIVE,
  ]);

  const { handleSubmit, setValue, ...formMethods } = useForm({
    mode: "all",
    defaultValues: {
      senderName: "",
      recipientName: "",
      recipientEmail: "",
      keyWord: filters?.keyWord || "",
      cardNumber: filters?.cardNumber || "",
    },
  });

  const methods = {
    ...formMethods,
    setValue,
    handleSubmit,
    control: formMethods.control,
    formState: formMethods.formState,
  };

  const loadDiscountCards = (page = currentPage, pageSizeParam = pageSize, filtersParam = filters) => {
    if (!userDetail?.UserAccountDetails?.UserID || !authenticationState?.accessToken) {
      return;
    }

    const requestData = {
      UserId: userDetail.UserAccountDetails.UserID,
      PagingInfoVM: {
        PageNumber: page,
        RowsPerPage: pageSizeParam,
        SearchColumns: [],
        SortColumns: {
          SortColumnName: "",
          SortOrder: ""
        }
      },
      KeyWord: filtersParam.keyWord || "",
      CardStatus: filtersParam.cardStatus || null,
      CardNumber: filtersParam.cardNumber || null,
    };

    httpPost(
      urlConstant.Travertex.GetUserTravelVertexDiscountCardList,
      requestData,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    ).then((response) => {
      if (response?.Value?.Status === dataConstant.APIResponseStatus.Success) {
        const responseData = response.Value.Data;
        const apiCards = responseData?.UserTravelVertexDiscountCards || [];
        const totalCount = responseData?.TotalCount;
        const activeCardsCount = responseData?.ActiveCardsCount;

        const mappedCards = apiCards.map(card => ({
          userTravelVertexDiscountCardID: card.UserTravelVertexDiscountCardID,
          acctID: card.AcctID,
          cardNumber: card.CardNumber,
          cardPin: card.CardPin,
          cardAmount: card.CardAmount,
          cardStatus: card.CardStatus,
          receiverEmailAddress: card.ReceiverEmailAddress,
          receiverName: card.ReceiverName,
          sendTimestamp: card.SendTimestamp,
        }));
        
        setLocalCards(mappedCards);
        setCurrentPage(page);
        setPageSize(pageSizeParam);
        setFilters(filtersParam);
        setTotalRecords(Math.max(totalCount || 0, mappedCards.length));
        setCardsAvailable(activeCardsCount);
        setMembershipLevel(userDetail?.PurchasePackageDetails?.LatestPackageType || "");
      } else {
        showSnackbar(response?.Value?.Message, "error");
      }
    }).catch((err) => {
      console.error(err);
    });
  };

  const sendDiscountCard = (cardData) => {
    if (!userDetail?.UserAccountDetails?.UserID || !authenticationState?.accessToken) {
      return;
    }

    const requestData = {
      LoginUserId: userDetail.UserAccountDetails.UserID,
      UserTravelVertexDiscountCardID: cardData.userTravelVertexDiscountCardID,
      SenderName: cardData.senderName,
      Email: cardData.email,
      Name: cardData.name,
    };

    httpPost(
      urlConstant.Travertex.SendTravelVertexDiscountCard,
      requestData,
      true,
      true,
      authenticationState.accessToken,
      authenticationState.impersonateEmail
    )
    .then((response) => {
      if (response?.Value.Status === dataConstant.APIResponseStatus.Success) {
        showSnackbar(response?.Value?.Message, "success");
        closeSendModal();
        window.location.reload();
      } else {
        showSnackbar(response?.Value?.Message, "error");
      }
    })
    .catch((err) => {
      console.error(err);
    });
  };

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.UserID && authenticationState?.accessToken) {
      loadDiscountCards(1, pageSize, filters);
    }
  }, [userDetail?.UserAccountDetails?.UserID, authenticationState?.accessToken]);

  const formConfig = {
    keywordField: {
      name: "keyWord",
      label: t("Common.Search Keyword"),
      type: "text",
      placeholder: t("TravelVertexDiscountCard.Search cards"),
      onCustomChange: (value) => handleFilterChange("keyWord", value)
    },
    cardNumberField: {
      name: "cardNumber",
      label: t("TravelVertexDiscountCard.Card Number"),
      type: "text",
      placeholder: t("TravelVertexDiscountCard.Enter card number"),
      onCustomChange: (value) => handleFilterChange("cardNumber", value)
    },
    cardStatusField: {
      name: "cardStatus",
      label: t("TravelVertexDiscountCard.Card Status"),
      type: "select",
      dropDownList: [
        { value: "", label: t("Common.All Status") },
        { value: "Active", label: t("Common.Active") },
        { value: "Inactive", label: t("Common.Inactive") }
      ],
      customOnChange: (selectedOption) => handleFilterChange("cardStatus", selectedOption?.value || ""),
    },
    senderNameField: {
      name: "senderName",
      label: t("TravelVertexDiscountCard.Sender Name"),
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true,
      type: "text",
    },
    recipientNameField: {
      name: "recipientName",
      label: t("TravelVertexDiscountCard.Recipient Name"),
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true,
      type: "text",
    },
    recipientEmailField: {
      name: "recipientEmail",
      label: t("TravelVertexDiscountCard.Recipient Email"),
      type: "email",
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true,
    },
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
    loadDiscountCards(1, pageSize, newFilters);
  };

  const handlePageChange = (_event, newPage) => {
    loadDiscountCards(newPage, pageSize, filters);
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    loadDiscountCards(1, newPageSize, filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      keyWord: "",
      cardStatus: "",
      cardNumber: "",
    };
    setValue("keyWord", "");
    setValue("cardNumber", "");
    setValue("cardStatus", " ");
    setFilters(defaultFilters);
    loadDiscountCards(1, pageSize, defaultFilters);
  };

  const handleRevealPin = (id) => {
    setPinRevealed((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openSendModal = (card) => {
    setSelectedCard(card);
    formMethods.reset({
      senderName: "",
      recipientName: "",
      recipientEmail: "",
    });
    setShowSendModal(true);
  };

  const closeSendModal = () => {
    setShowSendModal(false);
    setSelectedCard(null);
  };

  const openInfoModal = (card) => {
    setSelectedCardInfo(card);
    setShowInfoModal(true);
  };

  const closeInfoModal = () => {
    setShowInfoModal(false);
    setSelectedCardInfo(null);
  };

  const handleSendCard = (data) => {
    if (!selectedCard) return;

    sendDiscountCard({
      userTravelVertexDiscountCardID: selectedCard.userTravelVertexDiscountCardID,
      senderName: data.senderName,
      email: data.recipientEmail,
      name: data.recipientName,
    });

  };

  const getCardStatusColor = (status) => {
    switch (status) {
      case dataConstant.TravelVertexDiscountCardStatus.ACTIVE:
        return "success";
      case dataConstant.TravelVertexDiscountCardStatus.INACTIVE:
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Box width={"100%"} mt={'25px'}>
      <Card
        sx={(theme) => ({
          p: {
            xs: "20px",
            sm: "25px 30px",
          },
          background: theme.palette.background.cardGradient,
          borderRadius: "20px",
          height: "100%",
        })}
      >
        {/* Membership Info */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "common.black",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            boxShadow: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t("TravelVertexDiscountCard.Membership Level")}:{" "}
            <span style={{ fontWeight: 400 }}>{membershipLevel}</span>
          </Typography>
          <Chip
            label={`${t(
              "TravelVertexDiscountCard.Cards Available"
            )}: ${cardsAvailable}`}
            color="primary"
            sx={{ fontWeight: 500, fontSize: 16 }}
          />
        </Box>

        {/* Filter Controls */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "common.black",
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <Grid container spacing={2} alignItems={'flex-end'}>
            {/* <Grid size={{xs: 12, sm: 6, md: 3}}>
              <CustomTextFieldComponent
                methods={methods}
                properties={formConfig.keywordField}
              />
            </Grid> */}
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <CustomTextFieldComponent
                methods={methods}
                properties={formConfig.cardNumberField}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <CustomAutoCompleteDropdown
                methods={methods}
                properties={formConfig.cardStatusField}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClearFilters}
              >
                {t("Common.Clear")}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Card Grid/Table */}
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          {localCards.map((card) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 3 }}
              key={card.userTravelVertexDiscountCardID}
            >
              <Card
                sx={(theme) => ({
                  p: {
                    xs: "10px",
                    sm: "20px",
                  },
                  background: theme.palette.secondary.lightest,
                  borderRadius: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "100%",
                  gap: 2,
                })}
              >
                <Box sx={{ position: "relative", textAlign: 'center', width: '100%' }}>
                  <img
                    src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/website-logos/AventiLogo-White.png`}
                    alt="Travel Vertex Discount Card"
                    style={{
                      width: "calc(100% - 20px)",
                      height: "100px",
                      objectFit: "contain",
                      objectPosition: "center",
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  />
                </Box>

                <CardContent sx={{ width: '100%', p: "0px !important" }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    <Stack component="span" display={'inline-flex'} mr={1} color="secondary.light">{t("TravelVertexDiscountCard.Card")}:{" "}</Stack>
                    <span style={{ fontWeight: 400 }}>{card.cardNumber}</span>
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    <Stack component="span" display={'inline-flex'} mr={1} color="secondary.light">{t("TravelVertexDiscountCard.CardAmount")}:{" "}</Stack>
                    <span style={{ fontWeight: 400 }}>${card.cardAmount}</span>
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                    <Typography variant="body1" mr={1} color="secondary.light" sx={{ fontWeight: 500 }}>
                      {t("TravelVertexDiscountCard.PIN")}:
                    </Typography>
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      {pinRevealed[card.userTravelVertexDiscountCardID]
                        ? DecryptData(card.cardPin)
                        : "••••"}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() =>
                        handleRevealPin(card.userTravelVertexDiscountCardID)
                      }
                      sx={{ ml: 1, minWidth: 32 }}
                    >
                      {pinRevealed[card.userTravelVertexDiscountCardID]
                        ? t("Common.Hide")
                        : t("Common.Show")}
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Status Chip */}
                      <Chip
                        label={card.cardStatus}
                        color={getCardStatusColor(card.cardStatus)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />

                      {/* Action Buttons */}
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        {(card.ReceiverEmailAddress || card.ReceiverName || card.sendTimestamp) && (
                          <IconButton
                            color="info"
                            onClick={() => openInfoModal(card)}
                            size="small"
                            title={t("TravelVertexDiscountCard.Card Send Information")}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        )}

                        {/* Send Button */}
                        <IconButton
                          color="warning"
                          onClick={() => openSendModal(card)}
                          disabled={!sendableStatuses.includes(card.cardStatus)}
                          title={t("TravelVertexDiscountCard.Send Card")}
                          size="small"
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Pagination Controls */}
        {(totalRecords > 0 || localCards.length > 0) && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexWrap: "wrap",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
            }}
          >
            {/* Pagination */}
            <Pagination
              count={Math.ceil((totalRecords || localCards.length) / pageSize)}
              page={currentPage}
              onChange={handlePageChange}
              className='cm-pagination'
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                "& .MuiPagination-ul": {
                  justifyContent: "center",
                },
              }}
            />
            {/* Page Size Selector */}
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {t("Common.Rows per page")}:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={handleRowsPerPageChange}
                  variant="outlined"
                >
                  {[5, 10, 25, 50].map((size) => (
                    <MenuItem key={size} value={size}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                {t("Common.Total Gift Cards")}: {totalRecords || localCards.length}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Send Card Modal */}
        <Dialog
          open={showSendModal}
          onClose={closeSendModal}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: "20px", overflowX: "hidden" } }}
          sx={{ background: "transparent" }}
        >
          <form onSubmit={handleSubmit(handleSendCard)}>
            <Stack
              className="popup-title-icon"
              direction="row"
              alignItems="center"
              p={"16px 24px"}
              justifyContent="space-between"
              sx={(theme) => ({
                background: theme.palette.common.black,
                position: "relative",
              })}
            >
              <DialogTitle
                sx={{ fontWeight: 700, p: 0 }}
              >
                {t("TravelVertexDiscountCard.Send Card")}
              </DialogTitle>
              <IconButton
                onClick={closeSendModal}
                className="close-icon-bg"
                sx={(theme) => ({
                  bgcolor: theme.palette.grey[800],
                  color: theme.palette.common.white,
                  position: "static",
                  width: 36,
                  height: 36,
                  "&:hover": { bgcolor: theme.palette.grey[700] },
                  borderRadius: "50%",
                  zIndex: 2,
                })}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <DialogContent
              sx={(theme) => ({
                background: theme.palette.background.cardGradient,
                border: 0,
                borderBottomLeftRadius: "20px",
                borderBottomRightRadius: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                alignItems: "flex-start",
                boxSizing: "border-box",
                overflowX: "hidden",
              })}
              dividers
            >
              <Stack width={"100%"} gap={2}>
                <CustomTextField
                  methods={{ ...formMethods, setValue }}
                  properties={formConfig.senderNameField}
                />
                <CustomTextField
                  methods={{ ...formMethods, setValue }}
                  properties={formConfig.recipientNameField}
                />
                <CustomTextField
                  methods={{ ...formMethods, setValue }}
                  properties={formConfig.recipientEmailField}
                />
              </Stack>
              <Stack
                width="100%"
                borderTop={"1px solid var(--headerbg)"}
                direction="row"
                justifyContent="flex-end"
                pt={2}
                gap={2}
              >
                <Button
                  onClick={closeSendModal}
                  color="secondary"
                  variant="outlined"
                  sx={{ px: 3, fontWeight: 600 }}
                >
                  {t("Common.Cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  sx={{ px: 3 }}
                >
                  {t("Common.Send")}
                </Button>

              </Stack>
            </DialogContent>
          </form>
        </Dialog>

        {/* Info Modal */}
        <Dialog
          open={showInfoModal}
          onClose={closeInfoModal}
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "20px", overflowX: "hidden" } }}
          sx={{ background: "transparent" }}
        >
          <Stack
            className="popup-title-icon"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={(theme) => ({
              background: theme.palette.common.black,
              position: "relative",
              p: "16px 24px",
            })}
          >
            <DialogTitle
              sx={{ fontWeight: 700, color: "primary.main", p: 0 }}
            >
              {t("TravelVertexDiscountCard.Card Send Information")}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={closeInfoModal}
              className="close-icon-bg"
              sx={(theme) => ({
                position: "static",
                bgcolor: theme.palette.grey[800],
                color: theme.palette.common.white,
                width: 36,
                height: 36,
                "&:hover": { bgcolor: theme.palette.grey[700] },
                borderRadius: "50%",
                zIndex: 2,
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.cardGradient,
              border: 0,
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "flex-start",
              boxSizing: "border-box",
              overflowX: "hidden",
              p: 3,
            })}
            dividers
          >
            {selectedCardInfo && (
              <Stack width={"100%"} gap={3}>
                {/* Send Information */}
                {(selectedCardInfo.receiverName || selectedCardInfo.receiverEmailAddress || selectedCardInfo.sendTimestamp) && (
                  <Box>
                    <Grid container spacing={2}>
                      {/* Receiver Name */}
                      <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                        <Box display="flex" alignItems="center" flexWrap={'wrap'} gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t("TravelVertexDiscountCard.Receiver Name")}:
                          </Typography>
                          <Typography variant="body1" sx={{ color: "text.secondary" }}>
                            {selectedCardInfo.receiverName}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Receiver Email */}
                      <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                        <Box display="flex" flexWrap={'wrap'} alignItems="center" gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t("TravelVertexDiscountCard.Receiver Email")}:
                          </Typography>
                          <Typography variant="body1" sx={{ color: "text.secondary", wordBreak: 'break-all' }}>
                            {selectedCardInfo.receiverEmailAddress}
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Sent On */}
                      <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t("TravelVertexDiscountCard.Sent On")}:
                          </Typography>
                          <Typography variant="body1" sx={{ color: "text.secondary" }}>
                            {moment(selectedCardInfo.sendTimestamp).format(dataConstant.DateFormatArray[5])}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
        <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
      </Card>
    </Box>
  );
};

export default TravelVertexDiscountCardManagement;